/**
 * The account page's payloads, held from app-home startup so the page can draw its
 * panels the moment it opens rather than starting every fetch at that point (#1229).
 *
 * State only — no requests and no Angular. `account-prefetch-service` owns the
 * fetching; keeping the store here means its rules can be tested without a
 * `$httpBackend`.
 */

/** DI name of the service that fills this store. */
export const AccountPrefetchServiceName = 'gpAccountPrefetchService';

/** One key per request the account page issues when it opens. */
export type AccountDataKey = 'subscriptionInfo' | 'creditBalance' | 'creditPurchaseOptions';

interface StoredValue {
    value: unknown;
    fetchedAtMs: number;
}

export class AccountDataStore {
    /** null until an identity is known, so the first `setOwner` always takes. */
    private ownerKey: string | null = null;
    private values = new Map<AccountDataKey, StoredValue>();

    /**
     * Point the store at whoever is signed in. A different identity empties it:
     * these are one person's licences, invoices and credit balance, and signing a
     * second person into the same tab must not show them the first one's figures
     * while the refresh is still in flight.
     */
    setOwner(ownerKey: string): void {
        if (ownerKey === this.ownerKey) {
            return;
        }
        this.ownerKey = ownerKey;
        this.values.clear();
    }

    peek<T>(key: AccountDataKey): T | null {
        const stored = this.values.get(key);
        return stored ? stored.value as T : null;
    }

    put(key: AccountDataKey, value: unknown, nowMs: number): void {
        this.values.set(key, { value, fetchedAtMs: nowMs });
    }

    /** When `key` was last fetched, or null if it never was. */
    fetchedAtMs(key: AccountDataKey): number | null {
        const stored = this.values.get(key);
        return stored ? stored.fetchedAtMs : null;
    }

    clear(): void {
        this.values.clear();
        this.ownerKey = null;
    }
}
